import { StrictMode, useEffect } from "react";
import * as Sentry from "@sentry/react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "sonner";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

import "./styles.css";
import reportWebVitals from "./reportWebVitals.ts";
import { useAuthStore } from "./lib/auth";
import { queryClient } from "./lib/queryClient";

// AuthProvider component to handle auth hydration
function AuthProvider({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const refreshUser = useAuthStore((state) => state.refreshUser);
  const validateAuth = useAuthStore((state) => state.validateAuth);

  // Try to refresh user data when the component mounts
  useEffect(() => {
    const localToken = localStorage.getItem("auth_token");

    // Check both the state token and localStorage token
    if ((token || localToken) && !user) {
      refreshUser().catch((error) => {
        console.error("Failed to refresh user in AuthProvider:", error);
      });
    }
  }, [token, user, refreshUser]);

  // Add periodic token validation
  useEffect(() => {
    if (!token) return;

    // Initial validation
    validateAuth();

    // Set up periodic validation every 5 minutes
    const intervalId = setInterval(
      () => {
        validateAuth();
      },
      5 * 60 * 1000,
    );

    return () => clearInterval(intervalId);
  }, [token, validateAuth]);

  return <>{children}</>;
}

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Register the router context
declare module "@tanstack/react-router" {
  interface RouterContext {
    queryClient: QueryClient;
  }
}
Sentry.init({
  dsn: "https://7952cd2d7f8be5e0a68b391e279c2abc@o4509067127947264.ingest.us.sentry.io/4509067130568704",
});

// Render the app
const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
          <ReactQueryDevtools initialIsOpen={false} />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
